import { TileType } from './MapTemplates';

// TileType -> loaded HTMLImageElement[] mapping
const tileImages: Map<TileType, HTMLImageElement[]> = new Map();
let initialized = false;

// Callback to clear tile cache when images load
let onImageLoaded: (() => void) | null = null;

interface TileSpriteConfig {
  type: TileType;
  paths: string[]; // variant paths
}

const TILE_SPRITE_CONFIGS: TileSpriteConfig[] = [
  { type: TileType.GRASS, paths: ['/sprites/tiles/grass_0.png', '/sprites/tiles/grass_1.png', '/sprites/tiles/grass_2.png'] },
  { type: TileType.DIRT, paths: ['/sprites/tiles/dirt_0.png', '/sprites/tiles/dirt_1.png'] },
  { type: TileType.STONE, paths: ['/sprites/tiles/stone_0.png', '/sprites/tiles/stone_1.png'] },
  { type: TileType.SAND, paths: ['/sprites/tiles/sand_0.png', '/sprites/tiles/sand_1.png'] },
  { type: TileType.DARK_GRASS, paths: ['/sprites/tiles/dark_grass_0.png', '/sprites/tiles/forest_0.png'] },
  { type: TileType.MUD, paths: ['/sprites/tiles/mud_0.png'] },
  { type: TileType.HILL, paths: ['/sprites/tiles/hill_0.png', '/sprites/tiles/grass_2.png'] },
  { type: TileType.ROCKY_GROUND, paths: ['/sprites/tiles/rocky_0.png', '/sprites/tiles/stone_1.png'] },
  { type: TileType.CLIFF_EDGE, paths: ['/sprites/tiles/rocky_0.png'] },
  // No sprites for WATER, SNOW, LAVA, VOID, MOUNTAIN, SHALLOW_WATER, DEEP_WATER, SWAMP_WATER
  // These use procedural rendering as fallback
];

/**
 * Initialize tile sprite loading. Non-blocking - images load in background.
 * When an image loads, it calls the onImageLoaded callback to invalidate the tile cache.
 */
export function initTileSprites(cacheClearCallback: () => void): void {
  if (initialized) return;
  initialized = true;
  onImageLoaded = cacheClearCallback;

  for (const config of TILE_SPRITE_CONFIGS) {
    const images: HTMLImageElement[] = [];
    tileImages.set(config.type, images);

    for (const path of config.paths) {
      const img = new Image();
      img.onload = () => {
        if (onImageLoaded) {
          onImageLoaded();
        }
      };
      img.onerror = () => {
        // Image not found - procedural fallback will be used
      };
      img.src = path;
      images.push(img);
    }
  }
}

/**
 * Get a loaded tile image for the given type and variant.
 * Returns null if no image is available (not loaded yet or no sprite for this type).
 */
export function getTileImage(type: TileType, variant?: number): HTMLImageElement | null {
  const images = tileImages.get(type);
  if (!images || images.length === 0) return null;

  const idx = variant !== undefined ? variant % images.length : 0;
  const img = images[idx];

  // Only return if the image is actually loaded
  if (img && img.complete && img.naturalWidth > 0) {
    return img;
  }
  return null;
}
